'use client';

import React, { useState } from 'react';
import type { ElementType } from 'react';
import { Link } from '@skin-seoul/design-system';
import {
  formatCurrency,
  getCartItemQuantityBounds,
  type Currency,
  type CartItemResponse,
  CLASSNAME,
  productSizeAndPackTextConverter,
} from '@skin-seoul/service';
import { useDidUpdate, Condition, extractPathname } from '@skin-seoul/utils';
import CartNumberInput from './CartNumberInput';
import CartProductCardSkeleton from './CartProductCardSkeleton';

const CART_ITEM_CARD_TEST_ID = 'cart-item-card';
const REMOVE_ITEM_FROM_CART_TEST_ID = 'remove-item-from-cart';
const CART_ITEM_NUMBER_INPUT_TEST_ID = 'cart-item-number-input';

interface CartProductCardProps {
  item: CartItemResponse;
  packSavingsPercent?: number | null;
  packNudge?: React.ReactNode;
  onQuantityChange: (quantity: number) => void;
  onRemoveItem: () => void;
  quantityResetVersion?: number;
  showDivider?: boolean;
  isFirst?: boolean;
  isUpdatingQuantity?: boolean;
  isRemovalDisabled?: boolean;
  onImageClick?: (productLink: string) => void;
  currency: Currency;
  ImageComponent?: React.ComponentType<{ src: string; alt: string; width: number; height: number }>;
  linkComponent?: ElementType;
}

const CartProductCard = ({
  item,
  packSavingsPercent,
  packNudge,
  currency,
  onRemoveItem,
  onQuantityChange,
  quantityResetVersion,
  showDivider = true,
  isFirst = false,
  isUpdatingQuantity,
  isRemovalDisabled = false,
  ImageComponent = ({ src, alt, width, height }) => <img src={src} alt={alt} width={width} height={height} />,
  linkComponent,
  onImageClick,
}: CartProductCardProps) => {
  const LinkComponent = linkComponent || Link;
  const normalizedProductLink = extractPathname(item.productLink);
  const productSizeAndPackText = [item.itemData?.size, productSizeAndPackTextConverter({ pack: item.itemData?.pack })]
    .filter(Boolean)
    .join(', ');

  const [value, setValue] = useState<number>(item.quantity);
  const [failedThumbnail, setFailedThumbnail] = useState<string | null>(null);
  const thumbnail = item.image[0]?.thumbnail;
  const imageSource = thumbnail && thumbnail !== failedThumbnail ? thumbnail : (item.image[0]?.src ?? '');

  const { min: itemMin, max: itemMax } = getCartItemQuantityBounds(item);

  const handleRemoveItem = () => {
    onRemoveItem();
  };

  useDidUpdate(() => {
    setValue(item.quantity);
  }, [item.quantity, quantityResetVersion]);

  return (
    <div className={`w-full pb-4 ${isFirst ? 'pt-5' : 'pt-4'} ${showDivider ? 'border-b border-neutral-50' : ''}`}>
      <div className="flex w-full min-w-0 gap-4 relative px-0" data-testid={CART_ITEM_CARD_TEST_ID}>
        <LinkComponent
          href={normalizedProductLink}
          onClick={() => onImageClick?.(normalizedProductLink)}
          className="flex shrink-0 items-center justify-center w-[90px] h-28 bg-neutral-50 rounded-lg overflow-hidden cursor-pointer no-underline hover:no-underline"
        >
          <div
            className="flex items-center justify-center w-full h-full overflow-hidden [&_img]:mix-blend-multiply"
            onErrorCapture={() => {
              if (thumbnail && failedThumbnail !== thumbnail) {
                setFailedThumbnail(thumbnail);
              }
            }}
          >
            <ImageComponent src={imageSource} alt={item.name} width={90} height={112} />
          </div>
        </LinkComponent>

        <div className="flex min-w-0 flex-1 flex-col min-h-28 justify-between">
          <div className="mb-3 flex min-w-0 flex-col w-full gap-0.5">
            <div className="flex flex-row gap-1 items-center justify-between">
              <p className={`text-paragraph-medium font-regular min-w-0 flex-1 truncate ${CLASSNAME.NOT_TRANSLATE}`}>
                {item.name}
              </p>
              <button
                onClick={handleRemoveItem}
                disabled={isRemovalDisabled}
                type="button"
                aria-label={`Remove ${item.name} from cart`}
                data-testid={REMOVE_ITEM_FROM_CART_TEST_ID}
                className="inline-flex items-center justify-center shrink-0 h-6 w-6 border-0 bg-transparent p-0 hover:bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
              >
                <svg
                  aria-hidden="true"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-neutral-300"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="m15 9-6 6m0-6 6 6" />
                </svg>
              </button>
            </div>
            <Condition
              expression={Boolean(productSizeAndPackText)}
              then={
                <span className={`text-paragraph-xsmall text-neutral-600 ${CLASSNAME.NOT_TRANSLATE}`}>
                  {productSizeAndPackText}
                </span>
              }
            />
            {packSavingsPercent != null && packSavingsPercent > 0 && (
              <span className="inline-flex h-5 items-center self-start rounded bg-primary-50 px-1.5 font-medium text-label-small text-primary-500">
                Save {packSavingsPercent}%
              </span>
            )}
          </div>

          <div className="flex flex-wrap justify-between items-center gap-2">
            <fieldset disabled={isUpdatingQuantity} className="m-0 min-w-0 border-0 p-0">
              <CartNumberInput
                data-testid={CART_ITEM_NUMBER_INPUT_TEST_ID}
                min={itemMin}
                value={isNaN(value) ? '' : String(value)}
                defaultValue={String(item.quantity)}
                max={itemMax}
                onValueChange={({ valueAsNumber }) => {
                  setValue(valueAsNumber);
                  onQuantityChange(valueAsNumber);
                }}
              />
            </fieldset>
            <p
              className={`text-paragraph-medium font-medium ml-auto text-right break-words max-w-full ${CLASSNAME.NOT_TRANSLATE}`}
            >
              {formatCurrency(currency, item.price.salePrice)}
            </p>
          </div>
        </div>
      </div>
      {packNudge}
    </div>
  );
};

CartProductCard.Skeleton = CartProductCardSkeleton;

export default CartProductCard;
